// Where a request is on its way, for the progress bar and step strip (mockups 01 and 08). The
// status machine itself is shared/rules/status.ts; this only places a status on the visible steps.

import { TERMINAL, type PrStatus } from '../rules/statuses';

export const STEPS = [
  'submitted',
  'reviewed',
  'sourcing',
  'cfoApproval',
  'poIssued',
  'delivered',
  'closed',
] as const;
export type Step = (typeof STEPS)[number];

/** Steps completed; the step in progress is the next one. Side states keep where they paused. */
const STEP_OF: Record<PrStatus, number> = {
  Draft: 0,
  Cancelled: 0,
  Returned: 1,
  ProcurementReview: 1,
  WithDeptHead: 1,
  Rejected: 1,
  OnHold: 2,
  Sourcing: 2,
  PODraft: 2,
  PettyCash: 5,
  PendingCFO: 3,
  PendingVertical: 3,
  POSentPendingCFO: 5,
  POIssued: 5,
  PartlyReceived: 5,
  Received: 6,
  Invoiced: 6,
  VoucherWithFinance: 6,
  Paid: 6,
  Closed: 7,
};

/** Number of steps completed, 0–7. */
export function stepsDone(status: PrStatus): number {
  return STEP_OF[status];
}

export function isOpen(status: PrStatus): boolean {
  return status !== 'Draft' && !TERMINAL.includes(status);
}

/** Still open in the requester's eyes: delivered is the end for them (D100). */
export function isOpenForRequester(status: PrStatus): boolean {
  return isOpen(status) && status !== 'Received';
}

/** The requester has something to do: finish a draft or fix a returned request. */
export function waitsOnRequester(status: PrStatus): boolean {
  return status === 'Draft' || status === 'Returned';
}

/** Stopped for good before the end: no step is in progress. */
export function isStopped(status: PrStatus): boolean {
  return status === 'Rejected' || status === 'Cancelled';
}

// ---- Step strip per viewer (self-review B1–B8) ----

/** What the requester (and their department head) follow: no PO, CFO or payment steps (D64). */
export const REQUESTER_STEPS = ['submitted', 'approved', 'buying', 'delivered'] as const;
/** What Procurement, the CFO and Finance follow. */
export const STAFF_STEPS = STEPS;

export type StepState = 'done' | 'current' | 'todo' | 'attention' | 'hold' | 'stopped' | 'skipped';
export interface StepView {
  key: string;
  state: StepState;
}

/** Steps done for the requester, and the state of the step after them. */
const REQUESTER: Record<PrStatus, [number, StepState | null]> = {
  Draft: [0, 'attention'],
  Returned: [1, 'attention'],
  ProcurementReview: [1, 'current'],
  WithDeptHead: [1, 'current'],
  Rejected: [1, 'stopped'],
  Cancelled: [0, 'stopped'],
  Sourcing: [2, 'current'],
  OnHold: [2, 'hold'],
  PODraft: [2, 'current'],
  PendingCFO: [2, 'current'],
  PendingVertical: [2, 'current'],
  PettyCash: [2, 'current'],
  POSentPendingCFO: [3, 'current'],
  POIssued: [3, 'current'],
  PartlyReceived: [3, 'current'],
  // Delivery is the requester's last step (D100): invoices and payment are not theirs to follow.
  Received: [4, null],
  Invoiced: [4, null],
  VoucherWithFinance: [4, null],
  Paid: [4, null],
  Closed: [4, null],
};

/** Steps done for staff, and the state of the next one. */
const STAFF: Record<PrStatus, [number, StepState | null]> = {
  Draft: [0, 'attention'],
  Returned: [1, 'attention'],
  ProcurementReview: [1, 'current'],
  WithDeptHead: [1, 'current'],
  Rejected: [1, 'stopped'],
  Cancelled: [0, 'stopped'],
  Sourcing: [2, 'current'],
  OnHold: [2, 'hold'],
  PODraft: [2, 'current'],
  PendingCFO: [3, 'current'],
  PendingVertical: [3, 'current'],
  PettyCash: [5, 'current'],
  POSentPendingCFO: [3, 'current'],
  POIssued: [5, 'current'],
  PartlyReceived: [5, 'current'],
  Received: [6, 'current'],
  Invoiced: [6, 'current'],
  VoucherWithFinance: [6, 'current'],
  Paid: [6, 'current'],
  Closed: [7, null],
};

/**
 * The strip a viewer sees. Side states get their own marker instead of looking like progress:
 * a draft or a returned request waits on the requester, a held one is on hold, a rejected or
 * cancelled one stopped. Petty cash skips the CFO and PO steps (staff view).
 */
export function stripFor(status: PrStatus, staff: boolean): StepView[] {
  const keys: readonly string[] = staff ? STAFF_STEPS : REQUESTER_STEPS;
  const [done, next] = (staff ? STAFF : REQUESTER)[status];
  const skipped = staff && status === 'PettyCash' ? new Set(['cfoApproval', 'poIssued']) : null;
  return keys.map((key, i) => {
    if (skipped?.has(key)) return { key, state: 'skipped' };
    if (i < done) return { key, state: 'done' };
    if (i === done && next) return { key, state: next };
    return { key, state: 'todo' };
  });
}

/** For list progress bars: steps done out of the requester's steps. */
export function requesterProgress(status: PrStatus): { done: number; total: number } {
  return { done: REQUESTER[status][0], total: REQUESTER_STEPS.length };
}
